import React, { useState } from 'react'
import { ApolloError } from 'apollo-boost'
import { useLocationCapacities } from './hooks/useLocationCapacities'
import {
  Skeleton,
  MultiCompanyLocationSelect,
  PercentSelect,
  Button,
  IconButton,
  Toaster,
} from '@ui'
import { useReload } from '@hooks/reloadContext'
import { Divider } from '@blueprintjs/core'
import api, { ILocationCapacity, ILocation } from '@api'

export default function LocationCapacities() {
  const { capacities, isLoading } = useLocationCapacities()
  const [isAdding, setIsAdding] = useState(false)

  if (isLoading) {
    return <Skeleton />
  }

  return (
    <div className="flex flex-col">
      {capacities.map((cap, i) => (
        <LocationCapacityGroup cap={cap} key={i} />
      ))}
      <Divider />
      {isAdding ? (
        <NewLocationCapacityGroup onClose={() => setIsAdding(false)} />
      ) : (
        <Button text="Add New Group" onClick={() => setIsAdding(true)} minimal blue />
      )}
    </div>
  )
}

function LocationCapacityGroup({ cap }: { cap: ILocationCapacity }) {
  const [isLoading, setIsLoading] = useState(false)
  const reload = useReload()

  return (
    <div className="flex mt-8">
      <div style={{ width: 550, flexGrow: 1 }}>
        <div className=" text-grey-700 text-xs pl-3">Locations</div>
        <MultiCompanyLocationSelect
          values={cap.locations}
          onChange={() => {}}
          onAdd={(loc) => {
            setIsLoading(true)
            api.company.locationCapacities
              .add({ capacity: cap.capacity, locationIds: [loc.id] })
              .then(() => {
                Toaster.success({ message: 'Added Capacity to Location' })
                setIsLoading(false)
                reload.locationCapacities()
              })
              .catch((error: ApolloError) => {
                setIsLoading(false)
                Toaster.fail({ error })
              })
          }}
          onRemove={(locId) => {
            setIsLoading(true)
            api.company.locationCapacities
              .remove({ locationIds: [locId] })
              .then(() => {
                Toaster.success({ message: 'Removed Capacity from Location' })
                setIsLoading(false)
                reload.locationCapacities()
              })
              .catch((error: ApolloError) => {
                setIsLoading(false)
                Toaster.fail({ error })
              })
          }}
          noTitle
        />
      </div>
      <div className="flex">
        <div className="flex" style={{ minWidth: 251 }}>
          <div style={{ marginRight: 88 }}>
            <div className=" text-grey-700 text-xs">Capacity</div>
            <PercentSelect
              value={cap.capacity}
              onChange={(val) => {
                setIsLoading(true)
                api.company.locationCapacities
                  .update({
                    capacity: val.value,
                    locationIds: cap.locations.map(({ id }) => id),
                  })
                  .then(() => {
                    Toaster.success({
                      message: 'Updated Capacity for Locations',
                    })
                    setIsLoading(false)
                    reload.locationCapacities()
                  })
                  .catch((error: ApolloError) => {
                    setIsLoading(false)
                    Toaster.fail({ error })
                  })
              }}
            />
            <div className=" text-grey-700 text-xs whitespace-nowrap">
              Set up capacity, i.e. 10%, 20%, 35%
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-end pt-3">
          <IconButton
            iconName="times"
            onClick={() => {
              setIsLoading(true)
              api.company.locationCapacities
                .remove({ locationIds: cap.locations.map(({ id }) => id) })
                .then(() => {
                  Toaster.success({ message: 'Removed Capacity Group' })
                  setIsLoading(false)
                  reload.locationCapacities()
                })
                .catch((error: ApolloError) => {
                  setIsLoading(false)
                  Toaster.fail({ error })
                })
            }}
            tooltip="Remove Group"
            minimal
            loading={isLoading}
          />
        </div>
      </div>
    </div>
  )
}

function NewLocationCapacityGroup({ onClose }: { onClose: () => void }) {
  const [capacity, setCapacity] = useState(50)
  const [locations, setLocations] = useState<ILocation[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const reload = useReload()

  return (
    <div className="flex justify-between">
      <div style={{ width: 550 }}>
        <div className=" text-grey-700 text-xs pl-3">Locations</div>
        <MultiCompanyLocationSelect
          values={locations}
          onChange={() => {}}
          onAdd={(loc) => setLocations([loc, ...locations])}
          onRemove={(locId) => setLocations(locations.filter(({ id }) => id !== locId))}
          noTitle
        />
      </div>
      <div className="flex justify-between">
        <div style={{ marginRight: 50 }}>
          <div className=" text-grey-700 text-xs">Capacity</div>
          <PercentSelect value={capacity} onChange={(val) => setCapacity(val.value)} />
          <div className=" text-grey-700 text-xs whitespace-nowrap">
            Set up capacity, i.e. 10%, 20%, 35%
          </div>
        </div>

        <div className="flex flex-row justify-end pt-3">
          <IconButton
            style={{ marginRight: 5 }}
            iconName="times"
            tooltip="Cancel"
            minimal
            onClick={() => {
              onClose()
              setCapacity(50)
              setLocations([])
            }}
          />
          <IconButton
            iconName="check"
            onClick={() => {
              setIsLoading(true)
              api.company.locationCapacities
                .add({ capacity, locationIds: locations.map(({ id }) => id) })
                .then(() => {
                  Toaster.success({ message: 'Capacity Group Added' })
                  setIsLoading(false)
                  onClose()
                  reload.locationCapacities()
                })
                .catch((error: ApolloError) => {
                  setIsLoading(false)
                  Toaster.fail({ error })
                })
            }}
            tooltip="Create Group"
            minimal
            loading={isLoading}
          />
        </div>
      </div>
    </div>
  )
}
